import React, { useEffect, useState } from 'react';
import { Drawer, Descriptions, Tag, Button, Alert, Timeline, Spin, message, Collapse } from 'antd';
import { getEnvioCorreo, reintentarEnvioCorreo } from 'api/enviosCorreo';
import {
  ETIQUETAS_ESTADO,
  ETIQUETAS_TIPO,
  ETIQUETAS_MOTIVO,
  colorEstado,
  traducirErrorEnvio,
} from './etiquetas';

export default function EnvioCorreoDetailDrawer({ envioId, open, onClose, onReintentado }) {
  const [detalle, setDetalle] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [reintentando, setReintentando] = useState(false);

  useEffect(() => {
    if (!open || !envioId) return;
    let vigente = true;
    setCargando(true);
    getEnvioCorreo(envioId)
      .then((d) => {
        if (vigente) setDetalle(d);
      })
      .catch(() => {
        if (vigente) setDetalle(null);
      })
      .finally(() => {
        if (vigente) setCargando(false);
      });
    return () => {
      vigente = false;
    };
  }, [envioId, open]);

  const reintentar = async () => {
    setReintentando(true);
    try {
      const res = await reintentarEnvioCorreo(envioId);
      if (res?.estado === 'enviado') {
        message.success('El correo se envió correctamente');
      } else {
        message.warning('El reenvío no llegó a salir. Revisa el detalle del nuevo intento.');
      }
      onReintentado?.();
      onClose?.();
    } catch (e) {
      message.error(e?.response?.data?.message || 'No se pudo reenviar el correo');
    } finally {
      setReintentando(false);
    }
  };

  const destinatarios = detalle?.destinatarios || { to: [], cc: [] };

  return (
    <Drawer title="Detalle del envío" open={open} onClose={onClose} width={560}>
      {cargando && <Spin />}
      {!cargando && detalle && (
        <>
          {detalle.estado === 'fallido' && (
            <>
              <Alert
                type="error"
                showIcon
                style={{ marginBottom: detalle.error ? 8 : 16 }}
                message="Este correo no llegó a destino"
                description={traducirErrorEnvio(detalle.error)}
              />
              {detalle.error && (
                <Collapse
                  size="small"
                  style={{ marginBottom: 16 }}
                  items={[
                    {
                      key: 'detalle-tecnico',
                      label: 'Detalle técnico',
                      children: (
                        <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{detalle.error}</pre>
                      ),
                    },
                  ]}
                />
              )}
            </>
          )}
          {detalle.estado === 'omitido' && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message="Este correo no se envió"
              description={ETIQUETAS_MOTIVO[detalle.motivo_omision] || detalle.motivo_omision}
            />
          )}

          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="Tipo">
              {ETIQUETAS_TIPO[detalle.tipo] || detalle.tipo}
            </Descriptions.Item>
            <Descriptions.Item label="Estado">
              <Tag color={colorEstado(detalle.estado)}>
                {ETIQUETAS_ESTADO[detalle.estado] || detalle.estado}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Asunto">{detalle.asunto || '—'}</Descriptions.Item>
            <Descriptions.Item label="Para">
              {(destinatarios.to || []).join(', ') || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Con copia">
              {(destinatarios.cc || []).join(', ') || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Fecha">
              {detalle.createdAt ? new Date(detalle.createdAt).toLocaleString('es-CL') : '—'}
            </Descriptions.Item>
          </Descriptions>

          {Array.isArray(detalle.reintentos) && detalle.reintentos.length > 0 && (
            <>
              <h4 style={{ marginTop: 24 }}>Reenvíos</h4>
              <Timeline
                items={detalle.reintentos.map((r) => ({
                  color: colorEstado(r.estado),
                  children: `${new Date(r.createdAt).toLocaleString('es-CL')} — ${
                    ETIQUETAS_ESTADO[r.estado] || r.estado
                  }`,
                }))}
              />
            </>
          )}

          {detalle.reintentable && detalle.estado !== 'enviado' && (
            <Button
              type="primary"
              block
              loading={reintentando}
              onClick={reintentar}
              style={{ marginTop: 24 }}
            >
              Reenviar
            </Button>
          )}
          {!detalle.reintentable && (
            <Alert
              type="info"
              showIcon
              style={{ marginTop: 24 }}
              message="Este correo no se puede reenviar"
              description="Incluye una invitación de calendario que depende de la asignación actual. Para volver a avisar, repite la asignación."
            />
          )}
        </>
      )}
    </Drawer>
  );
}
